// StepKonfirmasi.tsx

import { useEffect, useState } from "react";
import ProgressIndicator from "@/components/private/ProgressIndicator";
import StepNavigation from "@/components/private/StepNavigation";
import { Card } from "@/components/ui/card";
import toast from "react-hot-toast";
import { axiosClient } from "@/lib/axiosClient";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { useQueryClient } from "@tanstack/react-query";
import type { RegistrationItem, RegistrationResponse } from "@/types/registration";

// Import komponen terpisah
import { KonfirmRegularView } from "@/components/private/order/KonfirmRegularView";
import { KonfirmPicView } from "@/components/private/order/KonfirmPicView";
import type { UserData } from "@/types/pic";

// --- Loading Skeleton Component ---
const LoadingSkeleton = () => (
    <div className="mx-auto py-12 space-y-10 pt-0 max-w-6xl animate-pulse">
        <Card className="border-0 shadow-lg bg-white dark:bg-gray-800 mb-3">
            <div className="text-center space-y-2 py-4">
                <div className="h-6 w-1/3 bg-gray-200 dark:bg-gray-700 mx-auto rounded" />
                <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 mx-auto rounded" />
            </div>
        </Card>
        <Card className="h-64 bg-gray-100 dark:bg-gray-800 rounded-xl" />
    </div>
);

export default function StepKonfirmasi() {
    const queryClient = useQueryClient(); // 2. Inisialisasi Query Client
    const { data: rawUser, isLoading: loadingUser } = useUser();
    const user = rawUser as UserData | null;
    const { data: activePeriod, isLoading: loadingPeriod } = useActivePeriod();

    const [registration, setRegistration] = useState<RegistrationItem | null>(null);
    const [loadingData, setLoadingData] = useState(true);

    // Fetch data pendaftaran
    useEffect(() => {
        const fetchRegistration = async () => {
            if (!user?.participant_id || !activePeriod?.id) return;
            if (user.role_id === 3) {
                setLoadingData(false);
                return;
            }
            try {
                const { data } = await axiosClient.get<RegistrationResponse>(
                    `/participants/${user.participant_id}/registrations/period/${activePeriod.id}`
                );
                setRegistration(data.data);
            } catch (error) {
                console.error("Gagal memuat data:", error);
            } finally {
                setLoadingData(false);
            }
        };
        fetchRegistration();
    }, [user, activePeriod]);

    // 3. Fungsi handleNext yang sudah disinkronkan dengan Guard
    const handleNext = async () => {
        if (!user?.participant_id || !activePeriod?.id) return;

        try {
            await toast.promise(
                axiosClient.put(
                    `/participants/${user.participant_id}/registrations/step/${activePeriod.id}`,
                    { current_step: 3 }
                ),
                {
                    loading: "Mengonfirmasi pendaftaran...",
                    success: "Konfirmasi berhasil!",
                    error: "Gagal memproses konfirmasi.",
                }
            );

            // ⚠️ PENTING: Invalidate cache agar StepGuard tahu kita sudah di Step 3
            await queryClient.invalidateQueries({ queryKey: ["registration-check"] });
            await queryClient.invalidateQueries({ queryKey: ["registration-step"] });

        } catch (error) {
            console.error("HandleNext Error:", error);
            throw error; // Lempar agar StepNavigation tidak pindah route jika gagal
        }
    };

    // 4. Fungsi handleBack untuk kembali ke Step 1
    const handleBack = async () => {
        if (!user?.participant_id || !activePeriod?.id) return;

        try {
            await axiosClient.put(
                `/participants/${user.participant_id}/registrations/step/${activePeriod.id}`,
                { current_step: 1 }
            );

            // Invalidate cache agar Guard mengizinkan balik ke step 1
            await queryClient.invalidateQueries({ queryKey: ["registration-step"] });
        } catch (error) {
            console.error("HandleBack Error:", error);
        }
    };

    const isLoading = loadingUser || loadingPeriod || (user?.role_id === 2 && loadingData);

    if (isLoading) return <LoadingSkeleton />;
    if (!activePeriod) return <div className="text-center py-10">Periode tidak aktif</div>;

    return (
        <div className="mx-auto py-12 space-y-8 pt-0 max-w-6xl">
            <Card className="relative border-0 shadow-lg bg-white dark:bg-gray-800 mb-6 overflow-hidden rounded-2xl transition-all duration-300">
                {/* 1. Ukiran Bali Sisi Kiri */}
                <img
                    src="/ornamen/ukiran-corner.png"
                    alt="Ukiran Bali"
                    className="absolute top-0 left-0 w-40 md:w-48 opacity-40 pointer-events-none z-0"
                />

                {/* 2. Ukiran Bali Sisi Kanan (Mirror) */}
                <img
                    src="/ornamen/ukiran-corner.png"
                    alt="Ukiran Bali"
                    className="absolute top-0 right-0 w-40 md:w-48 scale-x-[-1] opacity-40 pointer-events-none z-0"
                />

                {/* 3. Dot Pattern Layer */}
                <div
                    className="absolute inset-0 opacity-[0.1] dark:opacity-[0.05] pointer-events-none"
                    style={{
                        backgroundImage: `radial-gradient(currentColor 0.8px, transparent 0.8px)`,
                        backgroundSize: '12px 12px',
                    }}
                />

                {/* 4. Glow Blur Background Central (Nuansa Biru Lembut) */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-32 w-64 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

                {/* 5. Content Area */}
                <div className="relative z-10 px-10 md:px-16 py-8 flex flex-col items-center text-center space-y-3">
                    <div className="space-y-1">
                        <h2 className="text-xl font-black tracking-tight text-slate-900 dark:text-white flex items-center justify-center gap-2 uppercase">
                            Konfirmasi Data {user?.role_id === 3 ? "Tim / Group" : "Pendaftaran"}
                        </h2>

                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 leading-relaxed italic max-w-md mx-auto">
                            {user?.role_id === 3
                                ? "Periksa kembali daftar anggota tim Anda sebelum melanjutkan proses pembayaran kolektif."
                                : "Pastikan seluruh data pribadi Anda sudah benar sebelum melanjutkan ke pembayaran."
                            }
                        </p>
                    </div>
                </div>
            </Card>

            <ProgressIndicator currentStep={2} />

            {user?.role_id === 2 && registration ? (
                <KonfirmRegularView data={registration} user={user} />
            ) : user?.role_id === 3 ? (
                <KonfirmPicView activePeriodId={activePeriod.id} picName={user.name} />
            ) : (
                <div className="text-center py-10 text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    Data tidak ditemukan atau Role tidak valid.
                </div>
            )}

            <StepNavigation
                onBack={handleBack}
                onNext={handleNext}
            />
        </div>
    );
}